import keys from 'constants/keys'

/**
 * Action params processing system for different Jobs services
 *
 * How to add a new service:
 * 1. Create a class implementing the ActionParamsProcessor interface
 * 2. Add the new processor to ACTION_PARAMS_PROCESSORS
 * 3. Add handling in handleGetServiceParams in JobModal.tsx
 *
 * Examples:
 * - PopsActionParamsProcessor: extracts only pop_id and adds rid
 * - CompaignActionParamsProcessor: passes all parameters as is, adding rid
 * - DefaultActionParamsProcessor: used for unknown services
 */

// Action params processing system for different services
interface ActionParamsProcessor {
  processParams: (actionParams: any, rid: string) => any
}

class PopsActionParamsProcessor implements ActionParamsProcessor {
  processParams(actionParams: any, rid: string) {
    return {
      pop_id: actionParams?.pop_id || '',
      rid: rid,
    }
  }
}

class CompaignActionParamsProcessor implements ActionParamsProcessor {
  processParams(actionParams: any) {
    // For campaigns, pass all action_params as is, adding rid
    return {
      ...actionParams,
    }
  }
}

// Default handler for new services
class DefaultActionParamsProcessor implements ActionParamsProcessor {
  processParams(actionParams: any, rid: string) {
    return {
      ...actionParams,
      rid: rid,
    }
  }
}

// Registry of processors for different services
const ACTION_PARAMS_PROCESSORS: Record<string, ActionParamsProcessor> = {
  [keys.JOBS_SERVICES.POPS]: new PopsActionParamsProcessor(),
  [keys.JOBS_SERVICES.COMPAIGN]: new CompaignActionParamsProcessor(),
  // To add a new service:
  // [keys.JOBS_SERVICES.NEW_SERVICE]: new NewServiceActionParamsProcessor(),
}

export class ActionParamsProcessorManager {
  /**
   * Processes action_params depending on service type
   * @param serviceType - service type (pops, compaign, etc.)
   * @param actionParams - action parameters
   * @param rid - retail ID
   * @returns processed parameters
   */
  static processActionParams(serviceType: string, actionParams: any, rid: string): any {
    const processor = ACTION_PARAMS_PROCESSORS[serviceType] || new DefaultActionParamsProcessor()
    return processor.processParams(actionParams, rid)
  }

  /**
   * Returns list of supported services
   */
  static getSupportedServices(): string[] {
    return Object.keys(ACTION_PARAMS_PROCESSORS)
  }
}
